import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { Phone, MessageCircle, Eye, EyeOff, LifeBuoy } from "lucide-react";

const HERO_IMAGES = [
  { src: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1400&q=80&auto=format&fit=crop", caption: "Nutrisi Alami untuk Gut Health" },
  { src: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1400&q=80&auto=format&fit=crop", caption: "Sarapan Sehat Setiap Pagi" },
  { src: "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=1400&q=80&auto=format&fit=crop", caption: "Serat Tinggi, Energi Terjaga" },
  { src: "https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?w=1400&q=80&auto=format&fit=crop", caption: "Biji-bijian & Superfoods" },
  { src: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1400&q=80&auto=format&fit=crop", caption: "Makan Bersih, Hidup Aktif" },
];

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_hotto-inventory/artifacts/1jsxg7bu_logo%20hotto%20cianjur.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [slide, setSlide] = useState(0);
  const [helpInfo, setHelpInfo] = useState(null); // shown after 3 wrong

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setHelpInfo(null);
    try {
      await login(identifier, password);
      toast.success("Selamat datang!");
      nav("/");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        toast.error(detail.message || "Login gagal");
        if (detail.help) setHelpInfo(detail.help);
      } else {
        toast.error(typeof detail === "string" ? detail : errMsg(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo brand Hotto Cianjur (real logo) */}
          <div className="flex items-center gap-3 mb-8">
            <img
              src={LOGO_URL}
              alt="Hotto Branch Cianjur"
              className="w-16 h-16 rounded-full ring-2 ring-emerald-100 shadow-sm object-contain bg-white"
              data-testid="brand-logo"
            />
            <div>
              <div className="font-bold text-lg leading-tight" style={{fontFamily:'Outfit'}}>Hotto Branch Cianjur</div>
              <div className="text-xs text-emerald-700 italic font-medium">Gut Bless You</div>
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-2 tracking-tight" style={{fontFamily:'Outfit'}}>Masuk</h1>
          <p className="text-[var(--hotto-muted)] mb-8 text-sm">
            Portal internal untuk owner, staff, mitra, dan investor Hotto Cianjur.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="identifier">Email, Username, atau User ID</Label>
              <Input
                id="identifier" type="text" value={identifier}
                onChange={(e) => setIdentifier(e.target.value)} required
                data-testid="input-email" className="mt-1.5"
                placeholder="mis. owner atau HTX8Q1 atau owner@hotto.id"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password" type={showPw ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  data-testid="input-password" className="pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="btn-toggle-password" tabIndex={-1}
                  title={showPw ? "Sembunyikan password" : "Lihat password"}
                >
                  {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </div>

            {helpInfo && (
              <div className="p-3 bg-red-50 border-2 border-red-300 rounded-md" data-testid="help-card">
                <div className="flex items-center gap-2 mb-2">
                  <LifeBuoy className="w-4 h-4 text-red-600"/>
                  <b className="text-red-800 text-sm">Butuh Bantuan Login?</b>
                </div>
                <p className="text-xs text-red-700 mb-2">{helpInfo.note}</p>
                <div className="text-xs text-red-800 space-y-1">
                  <div><b>Identitas dicoba:</b> <span className="font-mono-num">{helpInfo.identifier_tried}</span></div>
                  <div><b>Pusat Bantuan:</b> {helpInfo.phone}</div>
                </div>
                <a
                  href={helpInfo.whatsapp_url}
                  target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md font-medium"
                  data-testid="btn-help-wa"
                >
                  <MessageCircle className="w-3 h-3"/> Hubungi via WhatsApp
                </a>
              </div>
            )}

            <Button type="submit" disabled={busy} data-testid="btn-login" className="w-full bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)] h-11">
              {busy ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          {/* Kontak Pengaduan */}
          <div className="mt-10 pt-6 border-t border-slate-100 text-center">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide mb-2">Butuh Bantuan?</div>
            <a href="https://wa.me/6287878286054" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 font-medium" data-testid="link-contact">
              <MessageCircle className="w-4 h-4"/>
              <span>Hubungi Kontak Pengaduan</span>
              <span className="font-mono-num font-bold">0878-7828-6054</span>
            </a>
            <div className="text-[11px] text-slate-400 mt-2">© Hotto Branch Cianjur · Gut Bless You</div>
          </div>
        </div>
      </div>

      {/* Right: image carousel */}
      <div className="hidden lg:block relative overflow-hidden bg-gradient-to-br from-emerald-900 to-slate-900">
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`}
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(6,95,70,0.35), rgba(15,23,42,0.75)), url("${img.src}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ))}

        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3 opacity-90">
            <img src={LOGO_URL} alt="" className="w-10 h-10 rounded-full ring-2 ring-white/40 object-contain bg-white/90"/>
            <div>
              <div className="font-bold" style={{fontFamily:'Outfit'}}>Hotto Cianjur</div>
              <div className="text-[11px] italic opacity-80">Gut Bless You</div>
            </div>
          </div>

          <div>
            <div className="text-white/60 text-xs mb-3 uppercase tracking-widest">Hidup Sehat Dimulai dari Perut yang Baik</div>
            <h2 className="text-4xl font-bold leading-tight mb-4" style={{fontFamily:'Outfit'}}>
              {HERO_IMAGES[slide].caption}
            </h2>
            <p className="text-white/80 max-w-md text-sm leading-relaxed">
              Serat &amp; nutrisi alami dari biji-bijian, dipilih untuk menjaga pencernaan dan energi harian Anda.
            </p>

            {/* dots */}
            <div className="flex gap-2 mt-8">
              {HERO_IMAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  className={`h-1.5 rounded-full transition-all ${i === slide ? "w-10 bg-white" : "w-2 bg-white/40 hover:bg-white/60"}`}
                  aria-label={`slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-white/60">
            <Phone className="w-3 h-3"/>
            <span>Pengaduan: 0878-7828-6054</span>
          </div>
        </div>
      </div>
    </div>
  );
}
